import {useState} from "react";
import {useNavigate, useParams} from "react-router";
import {PageHeader} from "@/layout/components/PageHeader.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {Card} from "@/components/ui/card.tsx";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {useGetSClassTestQuery} from "@/store/sclassTest.ts";
import {TestDetailsCard} from "@/pages/sclass-test/components/TestDetailsCard.tsx";
import {QuestionsList} from "@/pages/sclass-test/components/QuestionsList.tsx";
import {SClassTestListPanel} from "@/pages/sclass-test/components/SClassTestListPanel.tsx";

export function SClassTestEditPage() {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const sclassTestId = Number(id);

    const {data: test, isLoading, isError} = useGetSClassTestQuery(sclassTestId, {
        skip: !sclassTestId,
    });

    const [isDetailsDirty, setIsDetailsDirty] = useState(false);
    const [pendingTestId, setPendingTestId] = useState<number | null>(null);

    function handleSelectTest(targetId: number) {
        if (targetId === sclassTestId) return;
        if (isDetailsDirty) {
            setPendingTestId(targetId);
            return;
        }
        navigate(`/sclass-test/${targetId}/edit`);
    }

    function confirmDiscardAndNavigate() {
        if (pendingTestId != null) {
            navigate(`/sclass-test/${pendingTestId}/edit`);
        }
        setPendingTestId(null);
    }

    function handleDeletedSelected(nextId: number | null) {
        setIsDetailsDirty(false);
        if (nextId != null) {
            navigate(`/sclass-test/${nextId}/edit`, {replace: true});
        } else {
            navigate('/', {replace: true});
        }
    }

    return (
        <div className="font-normal px-9 py-9 space-y-6 max-w-[1400px] w-full mx-auto" dir="rtl">
            <PageHeader title={test?.description ? `עריכת מקבץ שאלות - ${test.description}` : 'עריכת מקבץ שאלות'}/>

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
                <SClassTestListPanel selectedId={sclassTestId} onSelect={handleSelectTest}
                                     onDeletedSelected={handleDeletedSelected}/>

                <div className="space-y-6 min-w-0">
                    {isLoading && (
                        <div className="flex justify-center py-20">
                            <Spinner/>
                        </div>
                    )}

                    {isError && (
                        <Card className="p-8 text-center text-sm text-red-600">
                            מקבץ השאלות המבוקש לא נמצא, או שאין לך הרשאה לצפות בו.
                        </Card>
                    )}

                    {test && (
                        <>
                            <TestDetailsCard test={test} onDirtyChange={setIsDetailsDirty}/>
                            <QuestionsList sclassTestId={test.id}/>
                        </>
                    )}
                </div>
            </div>

            <AlertDialog open={pendingTestId != null} onOpenChange={(open) => !open && setPendingTestId(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>שינויים שלא נשמרו</AlertDialogTitle>
                        <AlertDialogDescription>
                            ביצעת שינויים בפרטי מקבץ השאלות הנוכחי שעדיין לא נשמרו. מעבר למקבץ אחר יגרום לאובדן
                            השינויים. להמשיך בכל זאת?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDiscardAndNavigate}>
                            המשך בלי לשמור
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default SClassTestEditPage;

